/**
 * 财务健康 7 项 —— 服务端计算并**单接口下发**（规格 **§12** · 架构 **§5.5** · `DR-013` / `DR-015`）。
 *
 * ## 7 项定稿公式（含对 v1 的两处订正）
 *
 * | 项 | 公式 | 相对 v1 |
 * |---|---|---|
 * | 储蓄率 | `(窗口收入 − 窗口支出) ÷ 窗口收入` | 🔴 v1 分子是"资产收入比"，**不是储蓄率** |
 * | 投资率 | `投资市值 ÷ 资产总额`，**剔除保单现金价值** | 🔴 v1 分母用净资产（含负债） |
 * | 应急月数 | `货币资金 ÷ 近 3 个自然月月均支出` | — |
 * | 资产负债率 | `负债合计 ÷ 资产合计` | — |
 * | 消费比 | `近 3 月月均支出 ÷ 近 3 月月均收入` | — |
 * | 保费比 | `年度保费 ÷ 年度收入` | — |
 * | 贷款压力 | `每月债务偿还额 ÷ 近 3 月月均收入` | — |
 *
 * ## 四条纪律
 *
 * 1. 🔴 **「均值」两套概念分名**（`DR-013`）：**窗口内日均**（窗口总支出 ÷ 窗口天数含当天）
 *    ≠ **近 3 个自然月月均**（近 3 个完整自然月 ÷ 3）。本文件的三个输入都标了 `3m` ——
 *    **传错窗口就是口径错误**，调用方须自查。
 * 2. 🔴 **分母为 0 / 数据不足 ⇒ `sufficient = false`** —— UI 显示「**数据不足**」，
 *    **不显示 `0` 也不显示 `∞`**。
 * 3. 🔴 **不做健康分 / 不做评分 / 不做排名** —— 本文件**只产出数字**。
 * 4. 🔴 **呈现按三层组织，每层 = 一句陈述 + 一个动作**；动作必须**由该层数字算得出**
 *    （例："应急金 2.3 个月 → 每月多存 ¥1,500 可到 3 个月"），**泛泛建议不显示**，
 *    该层数据不足则**整层不显示**。⚠️ **文案组装属 `report` 模块**（本内核只给数字与
 *    `sufficient` 标记）—— 且**结构性规则不得进 E2「本月三件事」池**（防两处各提示一次）。
 */

import type { Cents, RatioE4 } from '../types/brand.js';
import { HealthPolicy } from '../constants/index.js';
import { divideExact, ratioE4, sumCents, toCents } from './money.js';

// ───────────────────────────── 输入 ─────────────────────────────

export interface HealthInput {
  /** 窗口收入（报表所选区间） */
  windowIncome: Cents;
  /** 窗口支出（**唯一「支出」口径**的结果，`composeExpense().total`） */
  windowExpense: Cents;
  /** 资产合计（`composeNetWorth().totalAssets`） */
  totalAssets: Cents;
  /** 负债合计（`composeNetWorth().totalLiabilities`） */
  totalLiabilities: Cents;
  /** 投资持仓市值（资产方第 10 类） */
  holdingsValue: Cents;
  /** 保单现金价值（资产方第 7 类；🔴 投资率分母须**剔除**它） */
  policyCashValue: Cents;
  /** 货币资金（资产方第 1 类） */
  monetaryAssets: Cents;
  /** 近 **3 个自然月**月均支出（≠ 窗口内日均） */
  avgMonthlyExpense3m: Cents;
  /** 近 **3 个自然月**月均收入 */
  avgMonthlyIncome3m: Cents;
  /** 年度保费（消费型 + 储蓄型） */
  annualPremium: Cents;
  /** 年度收入 */
  annualIncome: Cents;
  /** 每月债务偿还额 */
  monthlyDebtService: Cents;
}

// ───────────────────────────── 输出 ─────────────────────────────

export type HealthMetricKey =
  | 'savingsRate'
  | 'investmentRate'
  | 'emergencyMonths'
  | 'debtRatio'
  | 'consumptionRatio'
  | 'premiumRatio'
  | 'loanPressure';

export interface HealthMetric {
  key: HealthMetricKey;
  label: string;
  /**
   * 数值：**百分比类 = `E4`**（`3333` = 33.33%）；**应急月数 = 「月 × E4」**
   * （`23000` = 2.30 个月）。
   * 🔴 数据不足时 = `null`。
   */
  valueE4: RatioE4 | null;
  /** 🔴 `false` ⇒ UI 显示「数据不足」（**不显示 0 或 ∞**） */
  sufficient: boolean;
}

/** 三层组织（答"扛不扛得住 / 钱留不留得住 / 钱摆得对不对"） */
export interface HealthLineGroups {
  /** 风险线 */
  risk: readonly HealthMetricKey[];
  /** 效率线 */
  efficiency: readonly HealthMetricKey[];
  /** 结构线 */
  structure: readonly HealthMetricKey[];
}

export interface HealthResult {
  metrics: Readonly<Record<HealthMetricKey, HealthMetric>>;
  lines: HealthLineGroups;
}

/** 三层归属（架构 §5.5 定稿，顺序即呈现顺序） */
export const HEALTH_LINES: HealthLineGroups = {
  risk: ['emergencyMonths', 'debtRatio', 'loanPressure'],
  efficiency: ['savingsRate', 'consumptionRatio'],
  structure: ['investmentRate', 'premiumRatio'],
};

const LABELS: Record<HealthMetricKey, string> = {
  savingsRate: '储蓄率',
  investmentRate: '投资率',
  emergencyMonths: '应急月数',
  debtRatio: '资产负债率',
  consumptionRatio: '消费比',
  premiumRatio: '保费比',
  loanPressure: '贷款压力',
};

/** 分母 > 0 才可评估（🔴 否则「数据不足」，不显示 0 或 ∞） */
function metric(key: HealthMetricKey, numerator: Cents, denominator: Cents): HealthMetric {
  const den = denominator as number;
  const sufficient = den > 0;
  return {
    key,
    label: LABELS[key],
    valueE4: sufficient ? ratioE4(numerator, denominator) : null,
    sufficient,
  };
}

// ─────────────────────────── 主函数 ───────────────────────────

export function composeFinancialHealth(input: HealthInput): HealthResult {
  const { windowIncome, windowExpense, totalAssets, totalLiabilities } = input;

  // 投资率的分母：资产总额 **剔除保单现金价值**（防把"保障型资产"算成投资）
  const investableBase = toCents(
    (totalAssets as number) - (input.policyCashValue as number),
  ) as Cents;

  const savingsBase = toCents((windowIncome as number) - (windowExpense as number)) as Cents;

  const metrics: Record<HealthMetricKey, HealthMetric> = {
    // 储蓄率 =（窗口收入 − 窗口支出）÷ 窗口收入
    savingsRate: metric('savingsRate', savingsBase, windowIncome),
    // 投资率 = 投资市值 ÷（资产总额 − 保单现金价值）
    investmentRate: metric('investmentRate', input.holdingsValue, investableBase),
    // 应急月数 = 货币资金 ÷ 近 3 月月均支出
    emergencyMonths: metric('emergencyMonths', input.monetaryAssets, input.avgMonthlyExpense3m),
    // 资产负债率 = 负债合计 ÷ 资产合计
    debtRatio: metric('debtRatio', totalLiabilities, totalAssets),
    // 消费比 = 近 3 月月均支出 ÷ 近 3 月月均收入
    consumptionRatio: metric('consumptionRatio', input.avgMonthlyExpense3m, input.avgMonthlyIncome3m),
    // 保费比 = 年度保费 ÷ 年度收入
    premiumRatio: metric('premiumRatio', input.annualPremium, input.annualIncome),
    // 贷款压力 = 每月债务偿还额 ÷ 近 3 月月均收入
    loanPressure: metric('loanPressure', input.monthlyDebtService, input.avgMonthlyIncome3m),
  };

  return { metrics, lines: HEALTH_LINES };
}

/**
 * 「近 3 个自然月月均」—— 由**月度合计**求平均。
 *
 * 🔴 与「窗口内日均」**分名分口径**（`DR-013`）：本函数只做 `/ 月数`，
 * **不参与**窗口日均；窗口日均由消费方另算（`窗口总支出 ÷ 窗口天数含当天`）。
 *
 * @param monthlyTotals 近 N 个**完整**自然月的合计（缺月须补 0，不可跳过 —— 否则分母失真）
 */
export function averageMonthly(monthlyTotals: readonly Cents[]): Cents {
  if (monthlyTotals.length === 0) return 0 as Cents;
  const sum = sumCents(monthlyTotals) as number;
  // 唯一除法出口：`divideExact` 内部按「远离零」四舍五入（与金额舍入规则一致）
  return toCents(divideExact(sum, monthlyTotals.length)) as Cents;
}

/** 供 UI 判断"是否展示该层"（该层**全部**指标都不足 ⇒ 整层不显示） */
export function isLineVisible(result: HealthResult, line: keyof HealthLineGroups): boolean {
  return result.lines[line].some((k) => result.metrics[k].sufficient);
}

/** 近 3 个自然月的窗口月数（供调用方确认没有传错窗口） */
export const HEALTH_WINDOW_MONTHS = HealthPolicy.WindowMonths;
